import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, ProgressBar, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { readContract, writeContract, waitForTransactionReceipt } from '@wagmi/core';
import bountyContractABI from '../../contracts/tgetherConsensusBounty.json';
import LinkTokenABI from '../../contracts/LinkToken.json'; // Add LINK token ABI here
import { formatEther } from 'viem';
import * as config from '../../config.tsx';
import { parseEther } from 'viem';
const bountyContractAddress = process.env.REACT_APP_CONSENSUS_BOUNTY_CONTRACT_ADDRESS as `0x${string}`;
const linkTokenContract = process.env.REACT_APP_CHAINLINK_TOKEN_CONTRACT as `0x${string}`; // LINK token contract address

interface CreateBountyModalProps {
    show: boolean;
    handleClose: () => void;
    postId: string;
}

function CreateBountyModal({ show, handleClose, postId, }: CreateBountyModalProps) {
    const [bountyAmount, setBountyAmount] = useState<string | null>(null); // For bounty input
    const [consensusFee, setConsensusFee] = useState<string | null>(null); // For formatted fee
    const [transactionAlert, setTransactionAlert] = useState('');
    const [error, setError] = useState('');
    const [currentStep, setCurrentStep] = useState(1);
    const [feeChecked, setFeeChecked] = useState(false); // Indicates if LINK fee was approved
    const totalSteps = 3;

    const navigate = useNavigate();

    useEffect(() => {
        if (!show) {
            resetForm();
        }
    }, [show]);

    const resetForm = () => {
        setBountyAmount(null);
        setConsensusFee(null);
        setCurrentStep(1);
        setError('');
        setTransactionAlert('');
        setFeeChecked(false); // Reset fee approval status
    };

    const approveLinkFee = async () => {
        try {
            const tx = await writeContract(config.config, {
                address: linkTokenContract,   // LINK token contract address
                abi: LinkTokenABI,            // Correct ABI for the LINK token
                functionName: 'approve',      // The function name (approve)
                args: [process.env.REACT_APP_FUND_CONTRACT, BigInt("200000000000000000")], // Approve 0.2 LINK for the fee
                value: BigInt("0"),           // No value for this transaction
            });

            // Wait for the transaction to be mined
            const receipt = await waitForTransactionReceipt(config.config, {
                hash: tx,  // Transaction hash from the tx response
            });

            setConsensusFee('0'); // For Sepolia, we don't charge ETH fee directly
            setFeeChecked(true);  // Mark LINK fee as approved
        } catch (error) {
            console.error('Error approving LINK fee:', error);
            setError('Failed to approve LINK fee. Please try again.');
        }
    };

    const fetchConsensusFee = async () => {
        try {
            // Fetch the consensus fee from the Bounty contract
            const consensusFeeData = await readContract(config.config, {
                abi: bountyContractABI.abi,
                address: bountyContractAddress,
                functionName: 'fee',
            });

            const feeInWei = BigInt(consensusFeeData as string);
            const feeInEth = formatEther(feeInWei);

            setConsensusFee(feeInEth); // Set the consensus fee for display
            setError('');
            handleNext(); // Proceed to the next step
        } catch (error) {
            console.error('Error fetching consensus fee:', error);
            setError('Failed to fetch consensus fee. Please try again.');
        }
    };

    const handleNext = () => {
        if (currentStep < totalSteps) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePrev = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleCreateBounty = async () => {
        try {
            if (!postId || !bountyAmount) {
                setError('Please provide a valid bounty amount.');
                return;
            }

            const bountyValue = parseEther(bountyAmount);
            setTransactionAlert('Creating bounty... Please wait.');

            const tx = await writeContract(config.config, {
                abi: bountyContractABI.abi,
                address: bountyContractAddress,
                functionName: 'createBounty',
                args: [postId], // Passing the submission ID (postId)
                value: bountyValue, // Passing the bounty value
            });

            const receipt = await waitForTransactionReceipt(config.config, { hash: tx });

            setTransactionAlert('');
            navigate(`/search-results?type=Bounties&value=${encodeURIComponent(postId)}`);
            handleClose();
        } catch (error) {
            console.error('Error creating bounty:', error);

            if (error instanceof Error && error.message.includes('revert')) {
                setError('Transaction failed: Smart contract execution reverted.');
            } else if (error instanceof Error && error.message.includes('out of gas')) {
                setError('Transaction failed: Out of gas. Try increasing the gas limit.');
            } else {
                setError('Failed to create bounty. Please try again.');
            }

            setTransactionAlert('');
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <Form.Group>
                        <Form.Label>Bounty Amount (ETH)</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter bounty amount"
                            value={bountyAmount ?? ''}
                            onChange={(e) => setBountyAmount(e.target.value)}
                        />
                        {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
                    </Form.Group>
                );
            case 2:
                // If on Sepolia, ask for LINK approval
                if (process.env.REACT_APP_CHAIN_ID === '0x66eee') {
                    return (
                        <div>
                            <p>Press the button to approve the testnet LINK transfer. You can go to 
                                <a href="https://faucets.chain.link/arbitrum-sepolia" target="_blank" rel="noopener noreferrer">
                                    Arbitrum Sepolia Faucet
                                </a> to get funds.</p>
                            <Button style={{ marginBottom: "10px" }} variant="outline-info" onClick={approveLinkFee} disabled={feeChecked}>
                                Approve 0.2 LINK for Fee
                            </Button>
                            {feeChecked && <Alert variant="info" className="mt-3">LINK fee approved. You can proceed to create the bounty.</Alert>}
                        </div>
                    );
                } else {
                    return (
                        <>
                            <Button variant="secondary" onClick={fetchConsensusFee}>
                                Fetch Consensus Time & Fee
                            </Button>
                            {consensusFee && <Alert variant="info" className="mt-3">Consensus Fee: {consensusFee} ETH</Alert>}
                            {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
                        </>
                    );
                }
            case 3:
                return (
                    <div style={{ marginTop: '10px' }}>
                        <p>Consensus Fee: {consensusFee !== null ? `${consensusFee} ETH` : 'Fetching...'}</p>
                        <p style={{ color: '#6c757d', fontSize: '.9rem' }}>
                            *Verify before signing the transaction that the value matches below. Due to price volatility, we cannot guarantee automated upkeep.
                        </p>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>Create Bounty</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {transactionAlert && <Alert variant="info">{transactionAlert}</Alert>}
                <Form>
                    {renderStep()}
                    <ProgressBar now={(currentStep / totalSteps) * 100} className="my-3" />
                    <div className="d-flex justify-content-between">
                        <Button variant="outline-secondary" onClick={handlePrev} disabled={currentStep === 1}>
                            Previous
                        </Button>
                        {currentStep < totalSteps ? (
                            <Button variant="info" onClick={handleNext}>
                                Next
                            </Button>
                        ) : (
                            <Button variant="primary" onClick={handleCreateBounty} disabled={process.env.REACT_APP_CHAIN_ID === '0x66eee' && !feeChecked}>
                                Submit Bounty
                            </Button>
                        )}
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
}

export default CreateBountyModal;
